<template>
  <a-table
    class="member-info-table"
    :dataSource="data"
    :pagination="meta"
    :columns="columns"
    :row-key="(record) => record.id"
    :customRow="customRow"
    :loading="loading"
    @change="changePagination"
  >
    <template slot="id" slot-scope="id">
      <router-link
        class="text-primary"
        :to="`/referral/programs/${programId}/members/${isAffiliate ? 'referral' : 'affiliate'}/${id}`"
      >
        {{ id }}
      </router-link>
    </template>
    <template slot="login" slot-scope="login">
      <div
        class="d-flex align-items-center"
        @click.stop="copy(login, `${isAffiliate ? 'Referral' : 'Affiliate'} login copied to clipboard`)"
        style="width: fit-content;"
      >
        <a-icon
          type="copy"
          class="font-size-14 text-primary cursor-pointer mr-2"
        />
        <span class="cursor-pointer">
          {{ login || '-' }}
        </span>
      </div>
    </template>
    <template slot="phone" slot-scope="phone">
      <span>{{ phone ? phone : 'Not specified' }}</span>
    </template>
    <template slot="created_at" slot-scope="created_at">
      <span>{{ created_at ? formatDate(created_at) : '-' }}</span>
    </template>
    <template slot="started_at" slot-scope="started_at">
      <span>{{ started_at ? formatDate(started_at) : '-' }}</span>
    </template>
    <template slot="cashback" slot-scope="cashback">
      <div class="d-flex">
        <span v-if="cashback && typeof cashback.sum === 'number'" class="d-block mr-1">{{ cashback.sum }}</span>
        <span v-if="cashback && typeof cashback.percent === 'number'">{{ cashback.sum }} ({{ cashback.percent }}%)</span>
      </div>
    </template>
    <template slot="role" slot-scope="role">
      <a-badge
        v-if="role && role?.name"
        :count="role.name"
        style="cursor: default; transform: scale(1.2);"
        :number-style="{
          backgroundColor: role.name === 'Referral' ? '#52c41a' : '#108ee9',
          boxShadow: '0 4px 8px 0 #d9d9d9',
        }"
      />
      <span v-else>—</span>
    </template>
    <template slot="status" slot-scope="status">
      <div class="d-flex align-items-center">
        <a-badge
          :count="getStatusName(status)"
          style="cursor: default; transform: scale(1.2);"
          :number-style="{
            backgroundColor: getBackgroundStatus(status),
            boxShadow: '0 4px 8px 0 #d9d9d9',
          }"
        />
      </div>
    </template>
  </a-table>
</template>

<script>
import apiClient from '@/services/axios'
import { capitalizeFirstLetter } from '@/composables/useCapitalizeFirstLetter.js'
import { getMembersStatusColors } from '@/views/apps/referral-program/referral-programs/composables/useGetMembersStatusColors.js'
import { copyToClipboard } from '@/composables/copy/copyToClipboard.js'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    role: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    data: [],
    meta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: false,
    },
    filters: {
      current: undefined,
      pageSize: undefined,
    },
    loading: false,
  }),
  watch: {
    memberId: {
      handler(val, oldVal) {
        if (val === oldVal) {
          return
        }
        this.getParticipants()
      },
      deep: true,
    },
  },
  computed: {
    programId() {
      return this.$route.params.id
    },
    roleSystemName() {
      return this.$route.params.role
    },
    memberId() {
      return this.$route.params.member_id
    },
    isAffiliate() {
      return this.roleSystemName === 'affiliate'
    },
    columns() {
      const defaultColumns = [
        {
          title: 'ID',
          dataIndex: 'id',
          width: '10%',
          scopedSlots: { customRender: 'id' },
        },
        {
          title: 'Login',
          dataIndex: 'login',
          width: '10%',
          scopedSlots: { customRender: 'login' },
        },
        {
          title: 'Phone number',
          dataIndex: 'phone',
          width: '10%',
          scopedSlots: { customRender: 'phone' },
        },
        {
          title: 'Invitation date',
          dataIndex: 'created_at',
          width: '10%',
          ellipsis: true,
          scopedSlots: { customRender: 'created_at' },
        },
        {
          title: 'Start date',
          dataIndex: 'started_at',
          width: '10%',
          ellipsis: true,
          scopedSlots: { customRender: 'started_at' },
        },
        {
          title: 'Bets amount',
          dataIndex: 'bets_amount',
          width: '15%',
          scopedSlots: { customRender: 'bets_amount' },
        },
        {
          title: 'Cashback',
          dataIndex: 'cashback',
          width: '15%',
          scopedSlots: { customRender: 'cashback' },
        },
        {
          title: 'Status',
          dataIndex: 'status',
          width: '7%',
          scopedSlots: { customRender: 'status' },
        },
      ]
      if (this.role === 'affiliate') {
        return defaultColumns
      }
      defaultColumns.splice(defaultColumns.length - 1, 0, {
        title: 'Invited referrals',
        dataIndex: 'children_count',
        width: '15%',
        scopedSlots: { customRender: 'children_count' },
      })
      return defaultColumns
    },
    isFiltered() {
      return this.filters.current || this.filters.pageSize
    },
  },
  methods: {
    customRow(row) {
      return {
        on: {
          click: () => {
            this.$router.push(`/referral/programs/${this.programId}/members/${this.isAffiliate ? 'referral' : 'affiliate'}/${row.id}`)
          },
        },
      }
    },
    copy(value, message) {
      copyToClipboard(value, message)
    },
    formatDate(date) {
      return this.$moment(date).tz(this.$moment.tz.guess()).format('DD.MM.YYYY')
    },
    getStatusName(status) {
      return capitalizeFirstLetter(status)
    },
    getBackgroundStatus(status) {
      return getMembersStatusColors(status)
    },
    changePagination(pagination, _, sorter) {
      this.filters.current = pagination.current
      this.filters.pageSize = pagination.pageSize
      this.getParticipants()
    },
    getFilters() {
      const filters = {}

      if (!this.isFiltered) {
        return {}
      }

      if (this.filters.current) {
        filters.page = this.filters.current
      }
      if (this.filters.pageSize) {
        filters.per_page = this.filters.pageSize
      }

      return filters
    },
    async getParticipants() {
      try {
        this.loading = true

        const url = `${referralApiUrl}/referral-programs/${this.programId}/members/${this.roleSystemName}/${this.memberId}/participants`

        const filters = this.getFilters()

        const response = await apiClient.get(url, { params: filters })
        if (response?.data?.data) {
          this.data = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          if (!this.isFiltered && newMeta.total < 10) {
            this.meta = false
            return
          }
          Object.assign(this.meta, newMeta)
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    this.getParticipants()
  },
}
</script>

<style lang="scss">
.member-info-table {
  .ant-table-row {
    cursor: pointer !important;
  }
}
</style>
